import { ProjectsOverviewEmptyPage } from "@components/common/empty-page/projects-overview-empty-page";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { BulkChangeProjectStatus } from "@components/table/projects/bulk-actions/bulk-change-project-status";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { ProjectsTable } from "@components/table/projects/projects-table";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { useFetchProjects } from "@hooks/projects/use-fetch-projects";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ProjectsMap } from "@pages/projects/projects-map/projects-map";
import { ProjectsMobileView } from "@pages/projects/projects-mobile-view";
import { ProjectsTiles } from "@pages/projects/projects-tiles";
import { requiredProjectTableColumns } from "@pages/projects/projects-utils";
import { appStateSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** The archiving-state of the projects that will be shown */
  projectArchivingState: ProjectArchivingState;

  /**
   * Flag whether the content should be shown as loading regardless whether it was loaded.
   * This is useful when the parent component know that the content should be loading,
   * e.g. it is still loading the permissions.
   */
  shouldForceLoading: boolean;
}

/**
 * The projects tab listing all the active or archived projects (depending on the props) in the company
 */
export function ProjectsOverview({
  companyId,
  projectArchivingState,
  shouldForceLoading,
}: Props): JSX.Element {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));
  const { projects: projectsViewMode } = useAppSelector(dataViewModeSelector);
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);

  // Only allow map view mode if alpha testing is enabled.
  // If the view mode stored in local storage is map and alpha testing is disabled, default to cards view.
  // TODO - Remove once maps is released: https://faro01.atlassian.net/browse/ST-3944
  const viewMode = useMemo(() => {
    if (projectsViewMode === "map" && !isAlphaTestingEnabled) {
      return "cards";
    }
    return projectsViewMode;
  }, [projectsViewMode, isAlphaTestingEnabled]);

  const {
    projects,
    isFetchingProjects,
    hasFetchedFirstPage,
    nextPageCursor,
    sortBy,
    setSortBy,
    search,
    isDebouncingSearch,
    loadMoreProjects,
    totalCount,
  } = useFetchProjects({
    companyId,
    projectArchivingState,
    isEnabled: viewMode !== "map",
  });

  const { isScreenSmOrSmaller } = useMediaQueryList();

  /** Computes whether it is still loading to fetch all projects */
  const isLoading = useMemo(() => {
    return (
      // Set in loading state only when is fetching more projects.
      // This allows to not show the loading animation once the first page has been fetched.
      (isFetchingProjects && hasFetchedFirstPage) ||
      // For projects search only show the loading state when user is typing
      isDebouncingSearch ||
      shouldForceLoading ||
      !hasFetchedFirstPage
    );
  }, [
    isFetchingProjects,
    isDebouncingSearch,
    shouldForceLoading,
    hasFetchedFirstPage,
  ]);

  /** Flag whether the empty page should be shown, because it is no longer loading and does not have projects */
  const shouldShowEmptyPage = useMemo(() => {
    return (
      !shouldForceLoading &&
      !isFetchingProjects &&
      hasFetchedFirstPage &&
      projects.length === 0
    );
  }, [
    shouldForceLoading,
    isFetchingProjects,
    projects.length,
    hasFetchedFirstPage,
  ]);

  const isAnyRowSelected = selectedEntities.length !== 0;
  const shouldRenderActions = projects.length > 0;

  // First check if map view is enabled and do an early return to the map component.
  // The reason is that map view has different layout compared to the other views and
  // It should be treated independently.
  if (viewMode === "map") {
    return (
      <ProjectsMap
        companyId={companyId}
        projectArchivingState={projectArchivingState}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    );
  }

  return (
    <>
      <EntitiesToolbar
        toolbarEntityName="Project"
        numberOfRows={totalCount}
        isLoading={isLoading}
        sortActions={
          shouldRenderActions && (
            <ProjectsSort
              key="projects-sort"
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          )
        }
        exportComponent={
          shouldRenderActions && (
            <BulkExportProjects
              companyId={companyId}
              selectedProjects={isAnyRowSelected ? selectedEntities : projects}
              subjectType={"workspace"}
              projectStatus={projectArchivingState}
              areEntitiesSelected={isAnyRowSelected}
              buttonType="standard"
              viewMode={viewMode}
            />
          )
        }
      />

      {shouldShowEmptyPage && (
        <>
          <ProjectsOverviewEmptyPage
            projectArchivingState={projectArchivingState}
            isSearching={search !== undefined}
          />
          <BulkChangeProjectStatus
            projectStatus={projectArchivingState}
            projects={projects}
          />
        </>
      )}

      {isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <ProjectsMobileView
          projects={projects}
          sortBy={sortBy}
          isLoading={isLoading}
          projectArchivingState={projectArchivingState}
          loadMoreProjects={loadMoreProjects}
        />
      )}

      {viewMode === "list" && !isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <ColumnPreferencesProvider
          tableKey="projects-table"
          columns={requiredProjectTableColumns}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: [
              "name",
              "projectManager",
              "group",
              "created",
              "lastUpdated",
              "accessLevel",
            ],
          }}
        >
          <ProjectsTable
            projects={projects}
            companyId={companyId}
            isLoading={isLoading}
            nextPageCursor={nextPageCursor}
            loadMoreProjects={loadMoreProjects}
            sortBy={sortBy}
            setSortBy={setSortBy}
            projectArchivingState={projectArchivingState}
            totalCount={totalCount}
          />
        </ColumnPreferencesProvider>
      )}

      {viewMode === "cards" && !isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <ProjectsTiles
          projects={projects}
          companyId={companyId}
          isLoading={isLoading}
          loadMoreProjects={loadMoreProjects}
          sortBy={sortBy}
        />
      )}
    </>
  );
}
